import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createVNode as _createVNode, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" }
const _hoisted_2 = { class: "lg:hidden mt-4 text-center text-xs text-slate-500" }
const _hoisted_3 = { class: "font-semibold text-slate-700" }

import type { PaginacionFilterParams } from "@/utils/PaginacionFilterParams";
import { computed, ref } from "vue";
import ErrorState from "@/share/components/ErrorState.vue";
import EmptyState from "../components/modeloVehiculoComponents/EmptyState.vue";
import SkeletonTablas from "@/share/components/SkeletonTablas.vue";
import PaginationCompontens from "@/share/components/PaginationCompontens.vue";

// 🧩 Componentes UI
import FormularioModeloVehiculo from "../components/modeloVehiculoComponents/FormularioModeloVehiculo.vue";
import ModeloVehiculoTable from "../components/modeloVehiculoComponents/ModeloVehiculoTable.vue";
import {
  useModeloVehiculoCreate,
  useModeloVehiculoDelete,
  useModeloVehiculosList,
  useModeloVehiculoToggleVisib,
  useModeloVehiculoUpdate,
} from "../composables/modeloVehiculoComposables";
import { ModeloVehiculoCreateInterface } from "../interfaces/modelovehiculosinterfaces/ModeloVehiculoCreate.interface";
import FiltrosComponents from "@/share/components/filtrosComponents.vue";
import AdminPageShell from "@/share/components/admin/AdminPageShell.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'ModelosView',
  setup(__props) {

const params = ref<PaginacionFilterParams>({
  page: 1,
  limit: 10,
  isVisible: undefined,
  search: "",
});

const sortField = ref<
  | "nombreModelo"
  | "añoModelo"
  | "versionModelo"
  | "marca"
  | "isVisible"
  | "createdAt"
  | null
>(null);
const sortDirection = ref<"asc" | "desc">("asc");
const isFormularioOpen = ref(false);
const modeloEditId = ref<string | undefined>(undefined);
const isDelete = ref(false);
// 📦 Data principal (lista)
const {
  modelos,
  total,
  totalPages,
  currentPage,
  isPending: isLoadingList,
  isError,
  error,
  refetch,
} = useModeloVehiculosList(params);

// ⚙️ Mutaciones
const { mutate: crearModeloVehiculo, isPending: isCreatingModelo } =
  useModeloVehiculoCreate(() => {
    isFormularioOpen.value = false;
  });
const { mutate: updateModeloVehiculo, isPending: isUpdatingModelo } =
  useModeloVehiculoUpdate(() => {
    isFormularioOpen.value = false;
    modeloEditId.value = undefined;
  });

const isSavingModelo = computed(() =>
  modeloEditId.value ? isUpdatingModelo.value : isCreatingModelo.value
);
const { mutate: deleteModeloVehiculo } = useModeloVehiculoDelete(() => {
  isDelete.value = true;
  setTimeout(() => (isDelete.value = false), 100);
});
const { mutate: toggleVisibility } = useModeloVehiculoToggleVisib();

// 🔽 Ordenamiento
const modelosOrdenados = computed(() => {
  let data = [...modelos.value];

  const field = sortField.value; // variable local
  if (field) {
    data.sort((a, b) => {
      const aVal = a[field] ?? "";
      const bVal = b[field] ?? "";

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection.value === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection.value === "asc"
        ? aVal > bVal
          ? 1
          : -1
        : aVal < bVal
        ? 1
        : -1;
    });
  }

  return data;
});

// 🔍 Filtros y eventos
const handleSearch = (search: string) => {
  params.value.search = search;
  params.value.page = 1;
};

const handleVisibilityFilter = (visibility: number | undefined) => {
  params.value.isVisible = visibility;
  params.value.page = 1;
};

const handlePageChange = (page: number) => {
  params.value.page = page;
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const handleLimitChange = (limit: number) => {
  params.value.limit = limit;
  params.value.page = 1;
};

const handleSort = (
  field:
    | "nombreModelo"
    | "añoModelo"
    | "versionModelo"
    | "marca"
    | "isVisible"
    | "createdAt"
    
) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

// 🧩 Acciones CRUD
const handleToggleVisibility = (modeloId: string) => {
  toggleVisibility(modeloId);
};

const handleOpenCreate = () => {
  modeloEditId.value = undefined;
  isFormularioOpen.value = true;
};

const handleEditModelo = (modeloId: string) => {
  modeloEditId.value = modeloId;
  isFormularioOpen.value = true;
};

const handleSaveModelo = (modelo: ModeloVehiculoCreateInterface) => {
  crearModeloVehiculo(modelo);
};

const handleUpdateModelo = (
  id: string,
  Modelo: ModeloVehiculoCreateInterface
) => {
  updateModeloVehiculo({ idModelo: id, Modelo: Modelo });
};

const handleCloseFormulario = () => {
  isFormularioOpen.value = false;
  modeloEditId.value = undefined;
};

const handleDeleteModelo = async (modeloId: string) => {
  deleteModeloVehiculo(modeloId);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(AdminPageShell, {
    title: "Modelos de vehículos",
    description: "Administra los modelos por marca, año y versión."
  }, {
    actions: _withCtx(() => [
      _createElementVNode("button", {
        type: "button",
        onClick: handleOpenCreate,
        class: "w-full sm:w-auto px-4 sm:px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-shadow flex items-center justify-center gap-2"
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("svg", {
          class: "w-5 h-5 flex-shrink-0",
          fill: "none",
          stroke: "currentColor",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M12 4v16m8-8H4"
          })
        ], -1),
        _createElementVNode("span", null, "Nuevo modelo", -1)
      ]))])
    ]),
    default: _withCtx(() => [
      _createVNode(FormularioModeloVehiculo, {
        isOpen: isFormularioOpen.value,
        modeloId: modeloEditId.value,
        "is-saving": isSavingModelo.value,
        onClose: handleCloseFormulario,
        onSave: handleSaveModelo,
        onUpdate: handleUpdateModelo
      }, null, 8, ["isOpen", "modeloId", "is-saving"]),
      _createElementVNode("div", _hoisted_1, [
        _createVNode(FiltrosComponents, {
          search: params.value.search,
          visibility: params.value.isVisible,
          limit: params.value.limit,
          total: _unref(total),
          onSearch: handleSearch,
          onVisibilityFilter: handleVisibilityFilter,
          onLimitChange: handleLimitChange,
          onRefresh: _unref(refetch)
        }, null, 8, ["search", "visibility", "limit", "total", "onRefresh"]),
        (_unref(isLoadingList))
          ? (_openBlock(), _createBlock(SkeletonTablas, { key: 0 }))
          : (_unref(isError))
            ? (_openBlock(), _createBlock(ErrorState, {
                key: 1,
                error: _unref(error),
                onRetry: _unref(refetch)
              }, null, 8, ["error", "onRetry"]))
            : (_unref(modelos).length > 0)
              ? (_openBlock(), _createBlock(ModeloVehiculoTable, {
                  key: 2,
                  Modelos: modelosOrdenados.value,
                  "sort-field": sortField.value,
                  "sort-direction": sortDirection.value,
                  isDelete: isDelete.value,
                  currentPage: _unref(currentPage),
                  pageSize: params.value.limit,
                  onSort: handleSort,
                  onToggleVisibility: handleToggleVisibility,
                  onEditModelo: handleEditModelo,
                  onDeleteModelo: handleDeleteModelo
                }, null, 8, ["Modelos", "sort-field", "sort-direction", "isDelete", "currentPage", "pageSize"]))
              : (_openBlock(), _createBlock(EmptyState, { key: 3 })),
        (!_unref(isLoadingList) && !_unref(isError) && _unref(total) > 0)
          ? (_openBlock(), _createBlock(PaginationCompontens, {
              key: 4,
              "current-page": _unref(currentPage),
              "total-pages": _unref(totalPages),
              total: _unref(total),
              limit: params.value.limit!,
              onPageChange: handlePageChange
            }, null, 8, ["current-page", "total-pages", "total", "limit"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_2, [
        _cache[1] || (_cache[1] = _createTextVNode(" Total de modelos: ", -1)),
        _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(total)), 1)
      ])
    ]),
    _: 1
  }))
}
}

})